import { isBasketItemHeld } from '@store/basket/utils/isBasketItemHeld';
import { SpaPackageBasketItem } from 'shared-types/Basket';

type GuestLike = {
  id?: number | string | null;
  firstName?: string | null;
  lastName?: string | null;
};

type SelectionLike = {
  option?: { id?: number } | null;
  extra?: { id?: number } | null;
  startDate?: string | null;
  startTime?: string | null;
  guestDetails?: GuestLike | null;
};

type SelectionGroupLike = {
  isExtras?: boolean;
  configurationGroup?: { id?: number } | null;
  selections?: (SelectionLike | null)[] | null;
};

type SpaDayLike = {
  type?: string;
  spaPackage?: { id?: number } | null;
  checkIn?: string | null;
  expireAt?: string | null;
  bookable?: { status?: string } | null;
  note?: string | null;
  guestDetails?: (GuestLike | null)[] | null;
  selectionGroups?: (SelectionGroupLike | null)[] | null;
};

type GuestToken = (guest?: GuestLike | null) => string;

const listOf = <T>(value: unknown): T[] =>
  Array.isArray(value) ? value.filter((entry): entry is T => !!entry) : [];

const trimmed = (value?: string | number | null) => String(value ?? '').trim();

const toArrival = (checkIn: string) => {
  const [date, time = ''] = checkIn.trim().replace('T', ' ').split(' ');
  const minute = time.slice(0, 5);

  return minute && minute !== '00:00' ? `${date} ${minute}` : date;
};

const idOf = (guest: GuestLike) => `id:${trimmed(guest.id)}`;

const nameOf = (guest: GuestLike) =>
  `name:${[guest.firstName, guest.lastName].map((part) => trimmed(part).toLowerCase()).join('|')}`;

const requestedGuestToken: GuestToken = (guest) => {
  if (!guest) return '';
  return guest.id != null ? idOf(guest) : nameOf(guest);
};

const heldGuestToken = (requestedGuests: GuestLike[]): GuestToken => {
  const requestedIds = new Set(requestedGuests.filter((guest) => guest.id != null).map(idOf));

  return (guest) => {
    if (!guest) return '';
    return guest.id != null && requestedIds.has(idOf(guest)) ? idOf(guest) : nameOf(guest);
  };
};

const treatmentKeys = (spaDay: SpaDayLike, tokenOf: GuestToken) =>
  listOf<SelectionGroupLike>(spaDay.selectionGroups)
    .filter((group) => !group.isExtras)
    .flatMap((group) =>
      listOf<SelectionLike>(group.selections).map((selection) =>
        [
          trimmed(group.configurationGroup?.id),
          trimmed(selection.option?.id),
          trimmed(selection.startDate),
          trimmed(selection.startTime).slice(0, 5),
          tokenOf(selection.guestDetails),
        ].join('|')
      )
    );

const extraKeys = (spaDay: SpaDayLike, tokenOf: GuestToken) =>
  listOf<SelectionGroupLike>(spaDay.selectionGroups)
    .filter((group) => group.isExtras)
    .flatMap((group) =>
      listOf<SelectionLike>(group.selections).map((selection) =>
        [trimmed(selection.extra?.id), tokenOf(selection.guestDetails)].join('|')
      )
    );

const hasSameEntries = (held: string[], requested: string[]) => {
  const sortedHeld = [...held].sort();
  const sortedRequested = [...requested].sort();

  return (
    sortedHeld.length === sortedRequested.length &&
    sortedHeld.every((entry, index) => entry === sortedRequested[index])
  );
};

const addsNoNote = (held: SpaDayLike, requested: SpaDayLike) =>
  !trimmed(requested.note) || trimmed(requested.note) === trimmed(held.note);

const addsNothingTo = (held: SpaDayLike, requested: SpaDayLike) => {
  const heldGuests = listOf<GuestLike>(held.guestDetails);
  const requestedGuests = listOf<GuestLike>(requested.guestDetails);
  const heldToken = heldGuestToken(requestedGuests);

  return (
    held.spaPackage?.id === requested.spaPackage?.id &&
    typeof held.checkIn === 'string' &&
    typeof requested.checkIn === 'string' &&
    toArrival(held.checkIn) === toArrival(requested.checkIn) &&
    addsNoNote(held, requested) &&
    hasSameEntries(heldGuests.map(heldToken), requestedGuests.map(requestedGuestToken)) &&
    hasSameEntries(treatmentKeys(held, heldToken), treatmentKeys(requested, requestedGuestToken)) &&
    hasSameEntries(extraKeys(held, heldToken), extraKeys(requested, requestedGuestToken))
  );
};

export function withoutSpaDaysAlreadyInBasket(
  basketItems: unknown,
  spaDays: SpaPackageBasketItem[],
  now = Date.now()
): SpaPackageBasketItem[] {
  const heldSpaDays = listOf<SpaDayLike>(basketItems).filter(
    (item) => item.type === 'spa-packages' && isBasketItemHeld(item, now)
  );

  return spaDays.filter(
    (spaDay) => !heldSpaDays.some((held) => addsNothingTo(held, spaDay as SpaDayLike))
  );
}
